import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import LockedFeature from '../components/LockedFeature';
import { useAuth } from '../context/AuthContext';
import staffService from '../services/staffService';
import schoolsService from '../services/schoolsService';
import teacherService from '../services/teacherService';
import TeacherGradesSection from './teacher/TeacherGradesSection';
import TeacherAttendanceSection from './teacher/TeacherAttendanceSection';
import TeacherTimetableSection from './teacher/TeacherTimetableSection';
import TeacherAssignmentsSection from './teacher/TeacherAssignmentsSection';
import TeacherExtraClassesSection from './teacher/TeacherExtraClassesSection';
import TeacherPayslipsSection from './teacher/TeacherPayslipsSection';
import TeacherLibrarySection from './teacher/TeacherLibrarySection';
import TeacherModulesSection from './teacher/TeacherModulesSection';
import TeacherPTMSection from './teacher/TeacherPTMSection';
import TeacherChat from '../components/communication/TeacherChat';
import HRPerformancePage from './HRPerformancePage';
import {
  BookOpen, Clipboard, CalendarBlank, CheckSquare,
  User, ChatDots, Envelope, Briefcase, PlayCircle, UsersThree, ChartLineUp
} from '@phosphor-icons/react';
import './TeacherPortalPage.css';

function TeacherPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taughtClasses, setTaughtClasses] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(null);

  // Check if user has required role
  useEffect(() => {
    if (user && user.role !== 'teacher') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  // Load teacher profile and assignments
  useEffect(() => {
    if (!user?.school_id) return; // Wait for user to be loaded
    
    Promise.all([
      staffService.getMyClasses(),
      schoolsService.listTerms(user.school_id)
    ])
      .then(([classesData, termsData]) => {
        setProfile(user);
        setTaughtClasses(classesData.items || []);
        setAcademicTerms(termsData || []);
        
        // Set the first term as default
        if (termsData && termsData.length > 0) {
          setSelectedAcademicTerm(termsData[0].id);
        }
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load teacher data');
      })
      .finally(() => setLoading(false));
  }, [user]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page teacher-portal-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading teacher portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const pageContent = (
    <div className="page teacher-portal-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Teacher Portal</h1>
          <p>Manage grades, attendance, schedule, and assignments</p>
        </div>
        <div className="header-actions">
          <div className="user-info">
            <User size={20} />
            <span>{user?.first_name} {user?.last_name}</span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
          title="Dashboard"
        >
          <BookOpen size={18} />
          Dashboard
        </button>
        <button
          className={`tab-btn ${activeTab === 'grades' ? 'active' : ''}`}
          onClick={() => handleTabChange('grades')}
          title="Manage Grades"
        >
          <CheckSquare size={18} />
          Grades
        </button>
        <button
          className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => handleTabChange('attendance')}
          title="Mark Attendance"
        >
          <Clipboard size={18} />
          Attendance
        </button>
        <LockedFeature module="academic_reports">
          <button
            className={`tab-btn ${activeTab === 'timetable' ? 'active' : ''}`}
            onClick={() => handleTabChange('timetable')}
            title="View Schedule"
          >
            <CalendarBlank size={18} />
            Schedule
          </button>
        </LockedFeature>
        <LockedFeature module="homework_module">
          <button
            className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
            onClick={() => handleTabChange('assignments')}
            title="Manage Assignments"
          >
            <Clipboard size={18} />
            Assignments
          </button>
        </LockedFeature>
        <button
          className={`tab-btn ${activeTab === 'extra-classes' ? 'active' : ''}`}
          onClick={() => handleTabChange('extra-classes')}
          title="Manage Extra Classes"
        >
          <BookOpen size={18} />
          Extra Classes
        </button>
        <button
          className={`tab-btn ${activeTab === 'ptm' ? 'active' : ''}`}
          onClick={() => handleTabChange('ptm')}
          title="Parent-Teacher Meetings"
        >
          <UsersThree size={18} />
          PTM
        </button>
        <button
          className={`tab-btn ${activeTab === 'payslips' ? 'active' : ''}`}
          onClick={() => handleTabChange('payslips')}
          title="My Payslips"
        >
          <Briefcase size={18} />
          Payslips
        </button>
        <button
          className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => handleTabChange('performance')}
          title="My Performance"
        >
          <ChartLineUp size={18} />
          Performance
        </button>
        <button
          className={`tab-btn ${activeTab === 'library' ? 'active' : ''}`}
          onClick={() => handleTabChange('library')}
          title="E-Library"
        >
          <BookOpen size={18} />
          E-Library
        </button>
        <LockedFeature module="homework_module">
          <button
            className={`tab-btn ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => handleTabChange('modules')}
            title="Course Modules"
          >
            <PlayCircle size={18} />
            Course Modules
          </button>
        </LockedFeature>
        <button
          className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
          title="Chat with Parents"
        >
          <ChatDots size={18} />
          Chat
        </button>
      </div>

      {/* Page Content */}
      <div className="page-content">
        {activeTab === 'dashboard' && (
          <TeacherDashboard 
            profile={profile} 
            taughtClasses={taughtClasses}
            academicTerms={academicTerms}
            selectedAcademicTerm={selectedAcademicTerm}
            onTermChange={setSelectedAcademicTerm}
          />
        )}
        {activeTab === 'grades' && <TeacherGradesSection taughtClasses={taughtClasses} />}
        {activeTab === 'attendance' && (
          <TeacherAttendanceSection 
            taughtClasses={taughtClasses}
            academicTerms={academicTerms}
            selectedAcademicTerm={selectedAcademicTerm}
            onTermChange={setSelectedAcademicTerm}
          />
        )}
        {activeTab === 'timetable' && <TeacherTimetableSection taughtClasses={taughtClasses} />}
        {activeTab === 'assignments' && <TeacherAssignmentsSection taughtClasses={taughtClasses} />}
        {activeTab === 'extra-classes' && <TeacherExtraClassesSection taughtClasses={taughtClasses} />}
        {activeTab === 'ptm' && <TeacherPTMSection />}
        {activeTab === 'payslips' && <TeacherPayslipsSection />}
        {activeTab === 'performance' && <HRPerformancePage />}
        {activeTab === 'library' && <TeacherLibrarySection />}
        {activeTab === 'modules' && <TeacherModulesSection taughtClasses={taughtClasses} />}
        {activeTab === 'chat' && <TeacherChat />}
      </div>
    </div>
  );

  return <Layout>{pageContent}</Layout>;
}

/**
 * Teacher Dashboard Component
 */
const TeacherDashboard = ({ profile, taughtClasses, academicTerms, selectedAcademicTerm, onTermChange }) => {
  const { user } = useAuth();
  const [pendingGrades, setPendingGrades] = useState(0);
  const [activeAssignments, setActiveAssignments] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Fetch dashboard stats (includes pending grades)
        const dashboardData = await teacherService.getDashboard(selectedAcademicTerm);
        setPendingGrades(dashboardData.pending_grading || 0);

        // Fetch quick stats (includes published assignments)
        const statsData = await teacherService.getQuickStats(selectedAcademicTerm);
        setActiveAssignments(statsData.published_assignments || 0);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [selectedAcademicTerm]);

  return (
    <div className="teacher-dashboard">
      <div className="dashboard-grid">
        {/* Profile Card */}
        <div className="dashboard-card">
          <h3>My Profile</h3>
          <div className="profile-info">
            <div className="profile-item">
              <div className="profile-icon">
                <User size={20} />
              </div>
              <div className="profile-content">
                <div className="profile-label">Name</div>
                <div className="profile-value">{profile?.first_name || user?.first_name} {profile?.last_name || user?.last_name}</div>
              </div>
            </div>

            <div className="profile-item">
              <div className="profile-icon">
                <Envelope size={20} />
              </div>
              <div className="profile-content">
                <div className="profile-label">Email</div>
                <div className="profile-value">{profile?.email || user?.email}</div>
              </div>
            </div>

            <div className="profile-item">
              <div className="profile-icon">
                <Briefcase size={20} />
              </div>
              <div className="profile-content">
                <div className="profile-label">Role</div>
                <div className="profile-value">Teacher</div>
              </div>
            </div>
          </div>
        </div>

        {/* Classes Overview Card */}
        <div className="dashboard-card">
          <h3>Classes Assigned ({taughtClasses.length})</h3>
          {taughtClasses.length > 0 ? (
            <div className="classes-list">
              {taughtClasses.slice(0, 5).map(cls => (
                <div key={cls.class_id} className="class-item">
                  <span className="class-name">{cls.class_name}</span>
                  <span className="subject-name">{cls.subject_name}</span>
                </div>
              ))}
              {taughtClasses.length > 5 && (
                <p className="text-muted">+{taughtClasses.length - 5} more classes</p>
              )}
            </div>
          ) : (
            <p className="text-muted">No classes assigned</p>
          )}
        </div>

        {/* Quick Stats Card */}
        <div className="dashboard-card">
          <h3>Quick Stats</h3>
          <div className="stats-grid">
            <div className="stat">
              <div className="stat-value">{taughtClasses.length}</div>
              <div className="stat-label">Classes</div>
            </div>
            <div className="stat">
              <div className="stat-value">{loadingStats ? '—' : pendingGrades}</div>
              <div className="stat-label">Pending Grades</div>
            </div>
            <div className="stat">
              <div className="stat-value">{loadingStats ? '—' : activeAssignments}</div>
              <div className="stat-label">Assignments</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-btn">
            <CheckSquare size={24} />
            <span>Record Grades</span>
          </button>
          <button className="action-btn">
            <Clipboard size={24} />
            <span>Mark Attendance</span>
          </button>
          <button className="action-btn">
            <Clipboard size={24} />
            <span>Create Assignment</span>
          </button>
          <button className="action-btn">
            <CalendarBlank size={24} />
            <span>View Schedule</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherPortalPage;
